import { getVersionedFieldValueFromForm } from '@monorepo/shared/componentsV2/logs/EditEntryModal/helpers';
import * as Sentry from '@sentry/browser';
import { FieldResponse, SaveFieldValueRequest } from 'mapistry-shared';
import { useCallback } from 'react';
import { getFieldValueFromForm } from '../../componentsV2/fieldDataType/values/EditFieldValueFactory';
import { useToast } from '../../contexts';
import { useConfirmIncompleteLogEntries } from './useConfirmIncompleteLogEntries';

type UseConfirmBypassValidationAndSaveEntryProps<EntryResponseType> = {
  entryId?: string;
  entryType: 'resource' | 'logEntry';
  fields?: FieldResponse[];
  getEntryName?: (response: EntryResponseType) => string;
  onAfterSave?: () => void;
  saver: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formValues: Record<string, any>,
    fieldValues: SaveFieldValueRequest[],
  ) => Promise<EntryResponseType | undefined>;
};

export function useConfirmBypassValidationAndSaveEntry<EntryResponseType>({
  entryId,
  entryType,
  fields,
  getEntryName,
  onAfterSave,
  saver,
}: UseConfirmBypassValidationAndSaveEntryProps<EntryResponseType>) {
  const { success, showUserFriendlyErrorToast } = useToast();
  const { submitWithIncompleteChecks } = useConfirmIncompleteLogEntries();
  const entryTypeDisplayName =
    entryType === 'logEntry' ? 'Log entry' : 'Resource';

  const persistValidatedForm = useCallback(
    async (values) => {
      try {
        const fieldValuesToSave = (fields || []).map((field) =>
          field.isVersioned
            ? getVersionedFieldValueFromForm(field, values)
            : getFieldValueFromForm(field, values),
        );
        const saved = await saver(values, fieldValuesToSave);

        if (saved) {
          const entryName = getEntryName
            ? getEntryName(saved)
            : entryTypeDisplayName;
          success(`${entryName} has been ${entryId ? 'edited' : 'created'}.`);
          onAfterSave && onAfterSave();
        } else {
          Sentry.captureException(
            `Did not receive ${entryTypeDisplayName} back from create or edit request`,
            { extra: { values, entryId } },
          );
          throw new Error();
        }
      } catch (err) {
        showUserFriendlyErrorToast(
          err,
          `Unable to save ${entryTypeDisplayName}.`,
          { dontAutoHide: true },
        );
      }
    },
    [
      entryId,
      entryTypeDisplayName,
      showUserFriendlyErrorToast,
      fields,
      getEntryName,
      onAfterSave,
      saver,
      success,
    ],
  );

  const handleSave = useCallback(
    async ({ invalid, errors, values, handleSubmit }) => {
      await handleSubmit(); // validates and submits if valid
      // Resources have no soft-required fields, so there is nothing to bypass.
      if (!invalid || entryType === 'resource') return;
      await submitWithIncompleteChecks({
        entriesErrors: [errors ?? {}],
        submit: () => persistValidatedForm(values),
      });
    },
    [entryType, persistValidatedForm, submitWithIncompleteChecks],
  );

  return { handleSave, persistValidatedForm };
}
